import type { TFunction } from "i18next";

export interface RouteMetaOptions {
  folio?: string;
  /** Procedure display name — used as the contextual topbar title on surgery detail. */
  procedureName?: string;
}

export interface RouteMeta {
  /** Short label for the contextual topbar title. */
  title: string;
  /** When set, the topbar shows a back button to this path. */
  backTo?: string;
  /** Destination label for the back chip (defaults to `title` when omitted). */
  backLabel?: string;
  /** Browser tab segment (without the Suturia suffix). */
  documentTitle: string;
}

export function routeMeta(
  t: TFunction,
  pathname: string,
  options: RouteMetaOptions = {}
): RouteMeta {
  const { folio, procedureName } = options;

  if (pathname === "/") {
    return {
      title: t("common.nav.dashboard"),
      documentTitle: t("common.nav.dashboard"),
    };
  }
  if (pathname === "/library") {
    return {
      title: t("common.nav.library"),
      documentTitle: t("common.nav.library"),
    };
  }
  if (pathname === "/procedures") {
    return {
      title: t("common.nav.procedures"),
      documentTitle: t("common.nav.procedures"),
    };
  }
  if (pathname === "/community/new") {
    return {
      title: t("community.form.newTitle"),
      backTo: "/community",
      backLabel: t("common.nav.community"),
      documentTitle: t("community.form.newTitle"),
    };
  }
  if (pathname.endsWith("/edit") && pathname.startsWith("/community/")) {
    return {
      title: t("community.form.editTitle"),
      backTo: pathname.replace(/\/edit$/, ""),
      backLabel: t("common.nav.community"),
      documentTitle: t("community.form.editTitle"),
    };
  }
  if (pathname.startsWith("/community/")) {
    return {
      title: t("common.nav.community"),
      backTo: "/community",
      backLabel: t("common.nav.community"),
      documentTitle: t("common.nav.community"),
    };
  }
  if (pathname === "/community") {
    return {
      title: t("common.nav.community"),
      documentTitle: t("common.nav.community"),
    };
  }
  if (pathname.match(/^\/research\/datasets\/[^/]+\/tables\//)) {
    return {
      title: t("common.nav.researchDatasets"),
      backTo: pathname.replace(/\/tables\/[^/]+$/, ""),
      backLabel: t("common.nav.researchDatasets"),
      documentTitle: t("common.nav.researchDatasets"),
    };
  }
  if (pathname.startsWith("/research/datasets/")) {
    return {
      title: t("common.nav.researchDatasets"),
      backTo: "/research/datasets",
      backLabel: t("common.nav.researchDatasets"),
      documentTitle: t("common.nav.researchDatasets"),
    };
  }
  if (pathname === "/research/datasets") {
    return {
      title: t("common.nav.researchDatasets"),
      backTo: "/research",
      backLabel: t("common.nav.research"),
      documentTitle: t("common.nav.researchDatasets"),
    };
  }
  if (pathname.startsWith("/research/")) {
    return {
      title: t("common.nav.research"),
      backTo: "/research",
      backLabel: t("common.nav.research"),
      documentTitle: t("common.nav.research"),
    };
  }
  if (pathname === "/research") {
    return {
      title: t("common.nav.research"),
      documentTitle: t("common.nav.research"),
    };
  }
  if (pathname.startsWith("/onboarding")) {
    return {
      title: t("onboarding.profile.title"),
      documentTitle: t("onboarding.profile.title"),
    };
  }
  if (pathname === "/profile") {
    return {
      title: t("common.nav.profile"),
      documentTitle: t("common.nav.profile"),
    };
  }
  if (pathname === "/settings") {
    return {
      title: t("common.nav.settings"),
      documentTitle: t("common.nav.settings"),
    };
  }
  if (pathname === "/account") {
    return {
      title: t("common.nav.account"),
      backTo: "/profile",
      backLabel: t("common.nav.profile"),
      documentTitle: t("common.nav.account"),
    };
  }
  if (pathname === "/report") {
    return {
      title: t("common.nav.annualReport"),
      documentTitle: t("common.nav.annualReport"),
    };
  }
  if (pathname === "/new") {
    return {
      title: t("common.nav.newSurgery"),
      backTo: "/",
      documentTitle: t("common.nav.newSurgery"),
    };
  }
  if (pathname.startsWith("/edit/")) {
    const id = pathname.slice("/edit/".length);
    return {
      title: t("common.pageTitle.editProcedure"),
      backTo: id ? `/surgery/${id}` : "/library",
      documentTitle: folio
        ? t("common.pageTitle.editCrumb", { folio }).trim()
        : t("common.pageTitle.editProcedure"),
    };
  }
  if (pathname.startsWith("/surgery/")) {
    const title =
      procedureName ?? folio ?? t("common.pageTitle.procedureRecord");
    return {
      title,
      backTo: "/library",
      backLabel: t("common.nav.library"),
      documentTitle:
        procedureName ??
        (folio
          ? t("common.pageTitle.summaryCrumb", { folio }).trim()
          : t("common.pageTitle.procedureRecord")),
    };
  }
  return {
    title: t("common.nav.dashboard"),
    documentTitle: t("common.nav.dashboard"),
  };
}
